import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { RESOLVE_CLASH } from 'api/graphQL/mutations/bestPracticeClash.mutations'

interface UseResolveClashOptions {
    onCompleted?: (clashId: string) => void
    onError?: (error: Error) => void
}

interface UseResolveClashResult {
    resolveClash: (clashId: string) => Promise<void>
    loading: boolean
}

/**
 * Hook for resolving a Best Practice clash
 * Extracts mutation logic from ClashCard for reusability
 *
 * @param options - Configuration options including callbacks
 * @returns Resolve function and loading state
 */
export const useResolveClash = ({
    onCompleted,
    onError,
}: UseResolveClashOptions = {}): UseResolveClashResult => {
    const [resolveClashMutation, { loading }] = useMutation(RESOLVE_CLASH, {
        onCompleted: (data) => {
            const resolvedClashId = data?.resolveBestPracticeClash?.id
            if (resolvedClashId && onCompleted) {
                onCompleted(resolvedClashId)
            }
        },
        onError: (error) => {
            console.error('Error resolving clash:', error)
            onError?.(error)
        },
    })

    const resolveClash = useCallback(
        async (clashId: string): Promise<void> => {
            // Input validation
            if (!clashId || typeof clashId !== 'string' || clashId.trim() === '') {
                console.error('resolveClash called with invalid clashId:', clashId)
                onError?.(new Error('Invalid clash ID'))
                return
            }

            await resolveClashMutation({
                variables: { input: { clashId } },
            })
        },
        [resolveClashMutation, onError]
    )

    return {
        resolveClash,
        loading,
    }
}
